/*!
 * laneValue.js — the point of a drawn lane under the playhead
 * © 2026 Rob Canning — GPLv3
 *
 * An oscCtrl lane's value is the height of the path where the playhead is:
 * the point whose x is the playhead's x. Not the point at the playhead's
 * fraction of the path's length -- the two agree only on a path that
 * advances evenly, and a steep rise (M1000,1040 L1100,780 L3000,780) read
 * that way reached its top 160 units late: the musically important
 * gestures were the ones that came out wrong.
 *
 * The path is sampled once along its length (laneTable); each frame finds
 * the first stretch that crosses the playhead's x and bisects inside it
 * for the exact point (pointAtX). A lane is drawn left to right; one that
 * doubles back is read at its first crossing. Pure: `path` needs only
 * getTotalLength() and getPointAtLength(), so it can be tested without a
 * browser.
 */

/** x along the path at regular steps of length: { len, ls, xs }. */
export function laneTable(path) {
  const len = path.getTotalLength();
  const n = Math.max(64, Math.min(2048, Math.ceil(len / 4)));
  const ls = new Float64Array(n + 1), xs = new Float64Array(n + 1);
  for (let i = 0; i <= n; i++) {
    ls[i] = (len * i) / n;
    xs[i] = path.getPointAtLength(ls[i]).x;
  }
  return { len, ls, xs };
}

/**
 * The point on the path whose x is `x` (in the path's own units): the
 * first crossing, exact to a hundredth of a unit. Outside the path's
 * extent, the nearer end. Returns { x, y, at } (at: length along it).
 */
export function pointAtX(table, path, x) {
  const { ls, xs } = table;
  const n = xs.length - 1;
  for (let i = 0; i < n; i++) {
    const a = xs[i] - x, b = xs[i + 1] - x;
    if (a === 0) return { ...pt(path, ls[i]), at: ls[i] };
    if (a * b > 0) continue;
    // the crossing is in this stretch: bisect on which side of x we are
    let lo = ls[i], hi = ls[i + 1];
    for (let k = 0; k < 24 && hi - lo > 0.01; k++) {
      const mid = (lo + hi) / 2;
      if ((path.getPointAtLength(mid).x - x) * a > 0) lo = mid; else hi = mid;
    }
    return { ...pt(path, hi), at: hi };
  }
  // not crossed: the end nearer in x
  const end = Math.abs(xs[0] - x) <= Math.abs(xs[n] - x) ? 0 : n;
  return { ...pt(path, ls[end]), at: ls[end] };
}

const pt = (path, l) => { const p = path.getPointAtLength(l); return { x: p.x, y: p.y }; };
